import React, { useEffect, useMemo, useState } from 'react';
import { Typography, TextField, IconButton, Tooltip, Fade, Box } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TranslationResultProps } from './types';
import {
  analyzeStatementXmlSegments,
  StatementXmlEditor,
  serializeStatementXmlSegments,
  type StatementSegment,
} from '../statement-nodes';

export const TranslationResult: React.FC<TranslationResultProps> = ({
  translation,
  targetLanguage,
  targetLanguageDisplay,
  loading,
  onTranslationChange,
  onCopy,
  disableClpAssignment = false,
  referenceText,
  referenceLabel,
}) => {
  const { t } = useTranslation();
  const [xmlSegments, setXmlSegments] = useState<StatementSegment[] | null>(null);
  const [referenceSegments, setReferenceSegments] = useState<StatementSegment[] | null>(null);
  const translationAnalysis = useMemo(() => analyzeStatementXmlSegments(translation), [translation]);
  const displayTranslation = translationAnalysis.hasMissingGlossaryFields ? translationAnalysis.plainText : translation;

  useEffect(() => {
    if (!translation) {
      setXmlSegments(null);
      return;
    }
    setXmlSegments(translationAnalysis.hasNodes ? translationAnalysis.segments : null);
  }, [translation, translationAnalysis]);

  useEffect(() => {
    if (!referenceText) {
      setReferenceSegments(null);
      return;
    }
    const parsedReference = analyzeStatementXmlSegments(referenceText);
    setReferenceSegments(parsedReference.hasNodes ? parsedReference.segments : null);
  }, [referenceText]);

  if (!translation) return null;

  return (
    <Fade in>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {t('translation.generated')} ({targetLanguageDisplay})
          </Typography>
          <Tooltip title={t('translation.copyTranslation')}>
            <IconButton size="small" onClick={() => onCopy(translation)}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {xmlSegments ? (
          <StatementXmlEditor
            segments={xmlSegments}
            language={targetLanguage}
            onSegmentsChange={(next) => {
              setXmlSegments(next);
              onTranslationChange(serializeStatementXmlSegments(next));
            }}
            disabled={loading}
            disableClpAssignment={disableClpAssignment}
            referenceSegments={referenceSegments}
            referenceLabel={referenceLabel}
          />
        ) : (
          <TextField
            value={displayTranslation}
            onChange={(e) => onTranslationChange(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                lineHeight: 1.7,
                backgroundColor: '#fafafa',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
            }}
          />
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          {t('translation.reviewBeforeSaving')}
        </Typography>
      </Box>
    </Fade>
  );
};
